<template>
	<Grid rowGap="medium">
		<GridColumn>
			<FieldSelect
				name="dataType"
				:error="v$.type.$error"
				:options="dataTypesOptions"
				:model-value="formData.dataType"
				@update:model-value="handleDataTypeChange"
				placeholder="Tabular Data"
				:disabled="readOnly"
			>
				<Icon name="grid" />

				Data Type <a href="#" target="_blank">Type Definitions</a>
			</FieldSelect>
		</GridColumn>

		<GridColumn>
			<FieldSelect
				:error="v$.subtype.$error"
				:options="subTypeOptions"
				name="subType"
				v-model.trim="formData.subType"
				placeholder="Select"
				:disabled="readOnly"
			>
				<Icon name="grid" />

				Data Subtype
			</FieldSelect>
		</GridColumn>

		<GridColumn>
			<Checkboxes>
				<FieldCheckbox
					name="reuse"
					v-model="formData.reuse"
					isDropdown
					:disabled="readOnly"
				>
					This dataset is re-used from another public or private source
				</FieldCheckbox>

				<FieldCheckbox
					name="representativeImage"
					v-model="formData.representativeImage"
					isDropdown
					:disabled="readOnly"
				>
					This dataset is a Representative Image (or another type of media)
				</FieldCheckbox>

				<FieldCheckbox
					name="qc"
					v-model="formData.qc"
					isDropdown
					:disabled="readOnly"
				>
					This dataset was created for Quality Control (QC) or confirmatory purposes
				</FieldCheckbox>

				<FieldCheckbox
					name="hasReadMe"
					v-model="formData.hasReadMe"
					isDropdown
					:disabled="readOnly"
					:allowNA="true"
				>
					This dataset has a "Read Me"
				</FieldCheckbox>
			</Checkboxes>
		</GridColumn>

		<GridColumn>
			<Field
				name="URL"
				type="text"
				v-model.trim="formData.URL"
				placeholder="https://"
				:disabled="readOnly"
			>
				<Icon name="chain" color="currentColor" />

				Stable URL or other link to this dataset
			</Field>
		</GridColumn>

		<GridColumn>
			<Field
				name="PID"
				type="text"
				v-model.trim="formData.PID"
				placeholder="https://"
				:disabled="readOnly"
			>
				<Icon name="key" color="currentColor" />

				DOI, accession number, or permanent identifier (PID)
			</Field>
		</GridColumn>

		<GridColumn>
			<Field
				name="comments"
				v-model="formData.comments"
				:disabled="readOnly"
			>
				<Icon name="comment" />

				Additional Comments or Instructions
			</Field>
		</GridColumn>

		<GridColumn>
			<Field
				name="associatedFigureOrTable"
				v-model="formData.associatedFigureOrTable"
				:disabled="readOnly"
			>
				<Icon name="frame" />

				Associated Figure or Table
			</Field>
		</GridColumn>

		<GridColumn v-if="userRoleWeight >= 1000 && formData.reuse">
			<Field
				name="associatedReference"
				v-model="formData.associatedReference"
				:disabled="readOnly"
			>
				<Icon name="document" />

				Associated Reference
			</Field>
		</GridColumn>

		<GridColumn v-if="userRoleWeight >= 1000">
			<Field
				name="headSentence"
				v-model="formData.headSentence"
				:disabled="readOnly"
			>
				<Icon name="document" />

				Associated Head Sentence
			</Field>
		</GridColumn>
	</Grid>
</template>

<script>
/**
 * External Dependencies
 */
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex'

/**
 * Internal Dependencies
 */
import Icon from '@/components/icon/icon';
import Field from '@/components/field/field';
import Grid, { GridColumn } from '@/components/grid/grid';
import Checkboxes from '@/components/checkboxes/checkboxes';
import FieldSelect from '@/components/field-select/field-select';
import FieldCheckbox from '@/components/field-checkbox/field-checkbox';

export default {
	/**
	 * Name
	 */
	name: 'formDatasetsDefault',

	/**
	 * Components
	 */
	components: {
		Grid,
		Icon,
		Field,
		Checkboxes,
		GridColumn,
		FieldSelect,
		FieldCheckbox,
	},

	/**
	 * Props
	 */
	props: {
		formData: {
			type: Object,
			default: () => {}
		},
		readOnly: {
			type: Boolean,
			default: false
		}
	},

	/**
	 * Computed
	 */
	computed: {
		...mapGetters('account', ['userRoleWeight', ]),
		...mapGetters('pdfViewer', ['dataTypes']),
		dataTypesOptions() {		
			return Object.keys(this.dataTypes.dataTypes).map(key => ({
					value: key,
					label: key,
				}));
		},
		subTypeOptions() {
			const subTypes = this.dataTypes.dataTypes[this.formData.dataType];

			return subTypes.map(type => ({
					value: type,
					label: type,
				})).concat([{
					value: '',
					label: 'None',
				}]);
		}
	},

	/**
	 * Setup
	 */
	setup() {
		// @vuelidate/core is opt-in per component: this is what replaces
		// the global Vue.use(Vuelidate) that vuelidate 0.x installed.
		return { v$: useVuelidate() };
	},

	/**
	 * Validations
	 */
	validations: {
		type: {
			required
		},
		subtype: {
			required
		},
		permalink: {
			required
		}
	},
	
	/**
	 * Methods
	 */
	methods: {
		// Reset the sub type value when the main datatype changes
		handleDataTypeChange(value) {
			this.formData.dataType = value;
			this.formData.subType = '';
		}
	},
}
</script>

